// Turns YouTube and Vimeo URLs, in any of their public forms, into the
// canonical player URL embedded in help center articles and outbound posts.
// Keep in sync with app/models/video_embed.rb, the server-side counterpart.

const YOUTUBE_HOSTS = new Set([
  'youtube.com',
  'www.youtube.com',
  'm.youtube.com',
  'youtube-nocookie.com',
  'www.youtube-nocookie.com',
])
const VIMEO_HOSTS = new Set(['vimeo.com', 'www.vimeo.com', 'player.vimeo.com'])

const YOUTUBE_ID = /^[\w-]{11}$/
const VIMEO_ID = /^\d+$/

export type VideoProvider = 'youtube' | 'vimeo'

export interface VideoEmbed {
  provider: VideoProvider
  embedUrl: string
}

/** Canonical embed for a supported video URL, or null. */
export function parseVideoUrl(input: string): VideoEmbed | null {
  let url: URL
  try {
    url = new URL(input.trim())
  } catch {
    return null
  }
  if (url.protocol !== 'http:' && url.protocol !== 'https:') return null

  const host = url.hostname.toLowerCase()
  const segments = url.pathname.split('/').filter(Boolean)

  if (host === 'youtu.be') return youtube(segments[0])
  if (YOUTUBE_HOSTS.has(host)) {
    const id = segments[0] === 'watch' ? url.searchParams.get('v') : segments[segments.length - 1]
    return youtube(id)
  }
  if (VIMEO_HOSTS.has(host)) return vimeo(segments[segments.length - 1])
  return null
}

function youtube(id: string | null | undefined): VideoEmbed | null {
  if (!id || !YOUTUBE_ID.test(id)) return null
  return { provider: 'youtube', embedUrl: `https://www.youtube-nocookie.com/embed/${id}` }
}

function vimeo(id: string | undefined): VideoEmbed | null {
  if (!id || !VIMEO_ID.test(id)) return null
  return { provider: 'vimeo', embedUrl: `https://player.vimeo.com/video/${id}` }
}
